import { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebaseConfig';
import api from '../api/api';

export const useRegistration = (programId, program = null) => {
  const [config, setConfig] = useState(null);
  const [applications, setApplications] = useState([]);
  const [userApplications, setUserApplications] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Utility function to validate program ID
  const isValidProgramId = (id) => {
    if (!id) return false;
    // MongoDB ObjectId validation (24 character hex string)
    return /^[0-9a-fA-F]{24}$/.test(id);
  };

  // Enhanced error handling for invalid program IDs
  useEffect(() => {
    if (programId && !isValidProgramId(programId)) {
      console.error('❌ Invalid program ID format:', programId);
      console.error('Expected: 24-character MongoDB ObjectId');
      console.error('Got:', typeof programId, programId);
      setError('Invalid program ID format');
      setLoading(false);
    }
  }, [programId]);

  // Fetch registration configuration
  const fetchConfig = useCallback(async () => {
    if (!programId) {
      console.log('⚠️ No programId provided to fetchConfig');
      return;
    }

    console.log('=== FETCHING REGISTRATION CONFIG ===');
    console.log('Program ID:', programId);
    console.log('Program object:', program);

    try {
      const url = `/api/programs/${programId}/registration/config`;
      console.log('Fetching registration config from:', url);
      
      const data = await api.get(url);
      console.log('✅ Registration config fetched successfully:', data);
      
      setConfig(data.config || data); // Handle different response formats
      setPermissions(data.permissions || {});
    } catch (err) {
      console.log('❌ Error fetching registration config:', err);
      console.log('Error status:', err.status);
      console.log('Error message:', err.message);
      
      if (err.status !== 404) {
        setError(err.message);
        console.error('Non-404 error fetching registration config:', err);
        setConfig(null);
      } else {
        // If registration config endpoint doesn't exist (404), registration is disabled
        console.log('✅ Registration config endpoint not found (404), registration is disabled');
        console.log('This is expected for programs without registration enabled');
        setConfig({
          isRegistrationEnabled: false,
          capacity: program?.capacity || {}
        });
      }
      setPermissions({});
    }
  }, [programId, program]);

  // Fetch applications (automatically filtered by backend based on user permissions)
  const fetchApplications = useCallback(async () => {
    if (!programId) return;

    try {
      const data = await api.get(`/api/programs/${programId}/registration/applications`);
      setApplications(data.applications || []);
    } catch (err) {
      if (err.status !== 403) {
        setError(err.message);
        console.error('Error fetching applications:', err);
      }
      setApplications([]);
    }
  }, [programId]);

  // Fetch user's specific applications for this program
  const fetchUserApplications = useCallback(async () => {
    if (!programId) return;

    try {
      const data = await api.get(`/api/programs/${programId}/registration/my-applications`);
      setUserApplications(data);
    } catch (err) {
      if (err.status !== 403 && err.status !== 404) {
        console.error('Error fetching user applications:', err);
      }
      setUserApplications({ hasApplications: false, canApply: true, applications: [] });
    }
  }, [programId]);

  // Check if registration is enabled
  const isRegistrationEnabled = config?.isRegistrationEnabled || false;

  // Check if registration is open
  const isRegistrationOpen = () => {
    if (!config?.registrationPeriod) return true;
    
    const now = new Date();
    const start = new Date(config.registrationPeriod.startDate);
    const end = new Date(config.registrationPeriod.endDate);
    
    return now >= start && now <= end;
  };

  // Check if program is at capacity (checks both registration and program capacity)
  const isAtCapacity = () => {
    // Check registration capacity
    if (config?.capacity?.maxRegistrations) {
      const approvedCount = applications.filter(app => app.status === 'approved').length;
      if (approvedCount >= config.capacity.maxRegistrations) return true;
    }
    
    // Check program capacity (if available)
    if (program?.capacity?.maxParticipants) {
      const currentEnrolled = program.capacity.currentEnrolled || 0;
      if (currentEnrolled >= program.capacity.maxParticipants) return true;
    }
    
    return false;
  };

  // Get registration status for display
  const getRegistrationStatus = () => {
    if (!isRegistrationEnabled) return 'disabled';
    if (!isRegistrationOpen()) return 'closed';
    if (isAtCapacity()) return 'full';
    return 'open';
  };

  // Submit new application for withdrawn/removed users (bypasses existing application check)
  const submitNewApplicationForWithdrawnUser = async (applicationData) => {
    try {
      let result;
      
      // Force creation of new application regardless of existing applications
      // Handle FormData (for file uploads) vs regular objects
      if (applicationData instanceof FormData) {
        // Use direct fetch for FormData submissions
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('You must be logged in to submit an application.');
        }
        const authToken = await currentUser.getIdToken();
        
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/registration/apply`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`
            // Don't set Content-Type header - browser will set it with boundary for FormData
          },
          body: applicationData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        }

        result = await response.json();
      } else {
        // Use api.js for regular JSON submissions
        result = await api.post(`/api/programs/${programId}/registration/apply`, applicationData);
      }
      
      // Refresh both admin applications and user applications after successful submission
      await fetchApplications();
      await fetchUserApplications();
      
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  };

  // Submit application
  const submitApplication = async (applicationData) => {
    try {
      let result;
      
      // Check if user has an existing draft application (exclude rejected - create new for those)
      const existingDraftApplication = userApplications?.applications?.find(app => 
        app.status === 'draft'
      );
      
      // Handle FormData (for file uploads) vs regular objects
      if (applicationData instanceof FormData) {
        // Use direct fetch for FormData submissions
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('You must be logged in to submit an application.');
        }
        const authToken = await currentUser.getIdToken();
        
        let url, method;
        if (existingDraftApplication) {
          // Update existing draft application
          url = `${process.env.REACT_APP_BACKEND_URL}/api/registration/applications/${existingDraftApplication._id}`;
          method = 'PUT';
        } else {
          // Create new application
          url = `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/registration/apply`;
          method = 'POST';
        }
        
        const response = await fetch(url, {
          method: method,
          headers: {
            'Authorization': `Bearer ${authToken}`
            // Don't set Content-Type header - browser will set it with boundary for FormData
          },
          body: applicationData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        }

        result = await response.json();
      } else {
        // Use api.js for regular JSON submissions
        if (existingDraftApplication) {
          // Update existing draft application
          result = await api.put(`/api/registration/applications/${existingDraftApplication._id}`, applicationData);
        } else {
          // Create new application
          result = await api.post(`/api/programs/${programId}/registration/apply`, applicationData);
        }
      }
      
      // Refresh both admin applications and user applications after successful submission
      await fetchApplications();
      await fetchUserApplications();
      
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  };

  // Resubmit application (explicit resubmission endpoint)
  const resubmitApplication = async (applicationId) => {
    try {
      const result = await api.post(`/api/registration/applications/${applicationId}/resubmit`);
      
      // Refresh applications after resubmission
      await fetchApplications();
      await fetchUserApplications();
      
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  };

  // Approve application
  const approveApplication = async (applicationId, notes = '') => {
    try {
      const result = await api.post(`/api/registration/applications/${applicationId}/approve`, { notes });

      const actualStatus = result?.status || 'approved';

      // Optimistic update so UI reflects immediately
      setApplications(prev => prev.map(app =>
        (app._id === applicationId || app.id === applicationId)
          ? { ...app, status: actualStatus, reviewedAt: new Date().toISOString() }
          : app
      ));

      // Also refresh from backend for full consistency
      await fetchApplications();
      
      return { ...result, status: actualStatus };
    } catch (err) {
      throw new Error(err.message);
    }
  };

  // Reject application
  const rejectApplication = async (applicationId, reason, allowResubmission = true) => {
    try {
      const result = await api.post(`/api/registration/applications/${applicationId}/reject`, { 
        reason, 
        allowResubmission 
      });

      // Optimistic update so UI reflects immediately
      setApplications(prev => prev.map(app =>
        (app._id === applicationId || app.id === applicationId)
          ? { ...app, status: result?.status || 'rejected' }
          : app
      ));

      // Also refresh from backend for full consistency
      await fetchApplications();
      
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  };

  // Delete application
  const deleteApplication = async (applicationId) => {
    try {
      const result = await api.del(`/api/registration/applications/${applicationId}`);

      // Optimistic update: remove from list immediately
      setApplications(prev => prev.filter(app =>
        app._id !== applicationId && app.id !== applicationId
      ));

      // Also refresh from backend for full consistency
      await fetchApplications();
      await fetchUserApplications();
      
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  };

  // Get available templates
  const getTemplates = useCallback(async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? 
        `/api/registration/templates?${queryParams}` : 
        '/api/registration/templates';
      
      const data = await api.get(endpoint);
      return data;
    } catch (err) {
      throw new Error(err.message);
    }
  }, []);

  // Toggle registration (with automatic config creation if needed)
  const toggleRegistration = useCallback(async (isEnabled, availableTemplates = null) => {
    try {
      // If we're enabling registration and no config exists, create one first
      if (isEnabled && !config) {
        let templates = availableTemplates;
        
        // If no templates provided, try to fetch them
        if (!templates || templates.length === 0) {
          try {
            const templatesData = await getTemplates({ limit: 1 });
            templates = templatesData.templates || [];
          } catch (error) {
            console.error('Error fetching templates for toggle:', error);
            throw new Error('No templates available. Please create a template first.');
          }
        }
        
        if (!templates || templates.length === 0) {
          throw new Error('No templates available. Please create a template first.');
        }

        const templateId = templates[0]._id;

        // Create config with default settings matching backend API
        // Set isRegistrationEnabled to the desired state right from creation
        const defaultConfig = {
          templateId,
          isRegistrationEnabled: isEnabled, // Set the initial state here
          registrationPeriod: {
            startDate: new Date().toISOString(),
            endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
            lateRegistrationAllowed: false
          },
          capacity: {
            maxRegistrations: 50,
            waitlistEnabled: true,
            autoApprove: false
          },
          approvalWorkflow: {
            required: true,
            autoApproveConditions: {
              membershipRequired: false,
              paymentRequired: false,
              documentsRequired: false
            }
          },
          paymentConfig: {
            required: false,
            amount: 0,
            currency: 'USD',
            paymentMethods: ["card"]
          },
          notificationConfig: {
            confirmationEmail: { enabled: true },
            statusUpdateEmails: { enabled: true }
          },
          programSettings: {
            requireMembership: false,
            ageVerification: { required: false }
          }
        };

        // Create the config with isRegistrationEnabled already set
        const result = await api.post(`/api/programs/${programId}/registration/config`, defaultConfig);
        
        // Refresh config after creation
        await fetchConfig();
        
        return result;
      }

      // If config exists, use the toggle endpoint
      const result = await api.put(`/api/programs/${programId}/registration/config/toggle`, {
        isRegistrationEnabled: isEnabled
      });

      // Refresh config after toggle
      await fetchConfig();
      
      return result;
    } catch (err) {
      // ✅ Check for enhanced error response from backend
      // Note: api.js sets error.status and error.payload, not error.response
      if (err.status === 404 && err.payload?.action === 'create_config') {
        const errorData = err.payload;
        const suggestion = errorData.suggestion;
        
        console.log('📋 Registration config needed:', suggestion?.message);
        
        // Create enhanced error with suggestion
        const enhancedError = new Error(errorData.message);
        enhancedError.type = 'REGISTRATION_CONFIG_MISSING';
        enhancedError.suggestion = suggestion;
        enhancedError.canAutoCreate = !!suggestion?.templateId;
        enhancedError.programId = programId;
        throw enhancedError;
      } else {
        console.error('Error toggling registration:', err.message);
        throw new Error(err.message);
      }
    }
  }, [programId, config, getTemplates, fetchConfig]);

  // Auto-create registration configuration
  const autoCreateConfig = useCallback(async (templateId = null) => {
    try {
      let selectedTemplateId = templateId;
      
      // If no template ID provided, get the first available template
      if (!selectedTemplateId) {
        const templatesData = await getTemplates({ limit: 1 });
        const templates = templatesData.templates || [];
        
        if (templates.length === 0) {
          throw new Error('No templates available. Please create a template first.');
        }
        
        selectedTemplateId = templates[0]._id;
      }

      // Try the auto-create endpoint first
      try {
        const result = await api.post(`/api/programs/${programId}/registration/config/auto-create`, {
          templateId: selectedTemplateId
        });
        
        // Refresh config after creation
        await fetchConfig();
        
        return result;
      } catch (autoCreateError) {
        // If auto-create endpoint doesn't exist, fall back to manual creation
        if (autoCreateError.status === 404) {
          console.log('Auto-create endpoint not available, using manual creation');
          
          // Create config with default settings
          const defaultConfig = {
            templateId: selectedTemplateId,
            registrationPeriod: {
              startDate: new Date().toISOString(),
              endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              lateRegistrationAllowed: false
            },
            capacity: {
              maxRegistrations: 50,
              waitlistEnabled: true,
              autoApprove: false
            },
            approvalWorkflow: {
              required: true,
              autoApproveConditions: {
                membershipRequired: false,
                paymentRequired: false,
                documentsRequired: false
              }
            },
            paymentConfig: {
              required: false,
              amount: 0,
              currency: 'USD',
              paymentMethods: ["card"]
            },
            notificationConfig: {
              confirmationEmail: { enabled: true },
              statusUpdateEmails: { enabled: true }
            },
            programSettings: {
              requireMembership: false,
              ageVerification: { required: false }
            }
          };

          const result = await api.post(`/api/programs/${programId}/registration/config`, defaultConfig);
          
          // Refresh config after creation
          await fetchConfig();
          
          return result;
        } else {
          throw autoCreateError;
        }
      }
    } catch (err) {
      throw new Error(err.message);
    }
  }, [programId, getTemplates, fetchConfig]);

  // Update configuration
  const updateConfig = async (newConfig) => {
    try {
      let result;
      // Check if we have a real config (with _id) or just a placeholder config
      if (config && (config._id || config.isRegistrationEnabled)) {
        // Config exists in database - update it
        result = await api.put(`/api/programs/${programId}/registration/config`, newConfig);
      } else {
        // No config exists in database - create it with default values
        // Merge provided config with defaults matching backend API requirements
        const defaultConfig = {
          templateId: newConfig.templateId,
          registrationPeriod: {
            startDate: new Date().toISOString(),
            endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
            lateRegistrationAllowed: false
          },
          capacity: {
            maxRegistrations: 50,
            waitlistEnabled: true,
            autoApprove: false
          },
          approvalWorkflow: {
            required: true,
            autoApproveConditions: {
              membershipRequired: false,
              paymentRequired: false,
              documentsRequired: false
            }
          },
          paymentConfig: {
            required: false,
            amount: 0,
            currency: 'USD',
            paymentMethods: ["card"]
          },
          notificationConfig: {
            confirmationEmail: { enabled: true },
            statusUpdateEmails: { enabled: true }
          },
          programSettings: {
            requireMembership: false,
            ageVerification: { required: false }
          }
        };
        
        // Merge newConfig into defaults (newConfig values take precedence)
        const configToCreate = { ...defaultConfig, ...newConfig };
        result = await api.post(`/api/programs/${programId}/registration/config`, configToCreate);
      }

      // Refresh config after update
      await fetchConfig();
      
      return result;
    } catch (err) {
      throw new Error(err.message);
    }
  };

  // Refresh all data
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([fetchConfig(), fetchApplications(), fetchUserApplications()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchConfig, fetchApplications, fetchUserApplications]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    // Data
    config,
    applications,
    userApplications,
    permissions,
    loading,
    error,
    
    // Status checks
    isRegistrationEnabled,
    isRegistrationOpen: isRegistrationOpen(),
    isAtCapacity: isAtCapacity(),
    registrationStatus: getRegistrationStatus(),
    
    // User-specific checks
    canUserApply: userApplications?.canApply || false,
    hasUserApplications: userApplications?.hasApplications || false,
    userApplicationsList: userApplications?.applications || [],
    
    // Actions
    submitApplication,
    submitNewApplicationForWithdrawnUser,
    resubmitApplication,
    approveApplication,
    rejectApplication,
    deleteApplication,
    updateConfig,
    toggleRegistration,
    autoCreateConfig,
    getTemplates,
    refresh,
    fetchUserApplications,
    
    // Utilities
    pendingApplicationsCount: applications.filter(app => 
      ['submitted', 'under_review', 'documents_pending'].includes(app.status)
    ).length,
    approvedApplicationsCount: applications.filter(app => app.status === 'approved').length,
    totalApplicationsCount: applications.length,
  };
};

export default useRegistration;
